import { useEffect, useState } from "react"
import { match } from "ts-pattern"

import { inferOrgView } from "@/v2/components/layout/app-layout/AppLayoutContext"
import { irrInfo } from "@/v2/components/property/analysis/Utils"
import { IrrType, ViewType } from "@/v2/graphql/admin-generated-rq"
import {
  OrgSettingsDetailQuery,
  useSetOrgSettingsMutation,
} from "@/v2/graphql/public-generated-rq"
import {
  Button,
  EmptyState,
  RadioGroup,
  Section,
  Text,
  useToast,
} from "@lumen-energy/design-system"
import { IconAdjustmentsDollar, IconAlertTriangleFilled } from "@tabler/icons-react"
import { UseQueryResult } from "@tanstack/react-query"

import { Setting } from "./Setting"

export const OrgSettings = ({
  org,
  refetch,
}: {
  org: OrgSettingsDetailQuery["currentUser"]["org"]
  refetch: () => Promise<UseQueryResult<OrgSettingsDetailQuery, unknown>>
}) => {
  const [irrFlavor, setIrrFlavor] = useState<IrrType | undefined>(org?.irrFlavor)
  const [displayWarning, setDisplayWarning] = useState(false)
  const setOrgSettingsMutation = useSetOrgSettingsMutation()
  const toast = useToast()

  useEffect(() => {
    const changed = irrFlavor !== org?.irrFlavor
    setDisplayWarning(changed)
  }, [irrFlavor, org?.irrFlavor])

  // Mutation
  const setOrgSettings = async () => {
    if (!org) return
    const promise = setOrgSettingsMutation.mutateAsync({
      orgPid: org.pid,
      irrFlavor: irrFlavor || IrrType.PostTaxItcDeprIrr,
    })

    toast.promise(promise, {
      loading: "Saving settings...",
      success: "Settings saved successfully",
      error: "Error saving",
    })
    await promise
    refetch()
    setDisplayWarning(false)
  }

  const inferredView = inferOrgView(org)

  return (
    <div>
      {!org ? (
        <OrgSettingsEmptyState />
      ) : (
        match(inferredView)
          .with(ViewType.Investor, () => (
            <div className="flex flex-col gap-5">
              <Setting
                label="IRR display"
                description={`Configure the way you want your organization to see your financial returns.`}
              >
                <Text.LabelXs className="text-colors-text-tertiary">
                  Note: All IRR calculations include SRECs
                </Text.LabelXs>
                <RadioGroup.Root
                  name="irrFlavorSetting"
                  value={irrFlavor}
                  onValueChange={(val) => setIrrFlavor(val as IrrType)}
                  className="mt-2"
                >
                  {[
                    IrrType.PreTaxIrr,
                    IrrType.PreTaxItcIrr,
                    IrrType.PostTaxItcDeprIrr,
                  ].map((id) => (
                    <RadioGroup.Item key={id} value={id} label={irrInfo(id).expanded} />
                  ))}
                </RadioGroup.Root>
              </Setting>
              <Section.Root className="p-5">
                {displayWarning && (
                  <div className="flex-cols mb-3 flex items-center gap-2 text-colors-text-warning">
                    <IconAlertTriangleFilled
                      size={16}
                      className="text-colors-text-warning"
                    />
                    <Text.LabelXs>
                      This change will apply to all members of your organization
                    </Text.LabelXs>
                  </div>
                )}
                <Button
                  size="small"
                  disabled={!displayWarning}
                  onClick={setOrgSettings}
                >
                  Save Settings
                </Button>
              </Section.Root>
            </div>
          ))
          .otherwise(() => <OrgSettingsEmptyState />)
      )}
    </div>
  )
}

const OrgSettingsEmptyState = () => {
  return (
    <EmptyState
      title={"No organization settings"}
      icon={IconAdjustmentsDollar}
      className="border-none"
    />
  )
}
